import React, { useState } from "react";
import { TDetailedEmailForm, TTriageEmailDisassociateFromCaseForm, TTriageEmailMarkAsResolvedForm, TTriageSimpleEmailForm } from "../../../../../domain/codecs/form/TriageForm";
import { CRMUpdateChaseModeSectionPopout } from "../CRMUpdateChaseModeSectionPopout/CRMUpdateChaseModeSectionPopout";
import { TCaseUpdateChaseModeForm } from "../../../../../domain/codecs/form/CasesChaseTransitionForm";
import { useResolutionStatus } from "../../../hooks/useResolutionStatus";
import { CRMBottomControlBar } from "../../CRMBottomControlBar/CRMBottomControlBar";
import { CRMConfirmationCountdown, IRREVERSIBLE_CONFIRMATION_DURATION_MS } from "../CRMConfirmationCountdown/CRMConfirmationCountdown";
import { CRMEmailSingleViewBody } from "../CRMEmailSingleViewBody/CRMEmailSingleViewBody";
import { CRMEmailSingleViewControls } from "../CRMEmailSingleViewControls/CRMEmailSingleViewControls";
import { hasFilterableAttachments } from "../../Simple/CRMEmailAttachment/CRMEmailAttachment";

type TResolutionStatus =
    "disassociate"
    | "resolve"
    | "none"
;

type TResolutionData = {}

type TEmailViewStatus = 
    "email-body"
    | "update-chase-mode"
;

type TCaseEmailSingleViewUnresolvedProps = {
    count: number;
    email: TTriageSimpleEmailForm;

    isShowingNext: boolean;
    isShowingPrevious: boolean;
    
    onDisassociate: (form: TTriageEmailDisassociateFromCaseForm) => void;
    onResolve: (form: TTriageEmailMarkAsResolvedForm) => void;
    onChangeChaseModeForm: (form: TTriageEmailMarkAsResolvedForm) => void;
    onClose: () => void;
    onNext: () => void;
    onPrevious: () => void;

    onReply: (form: TDetailedEmailForm) => void;
    onForward: (form: TDetailedEmailForm) => void;
    onFileAttachments: (form: TDetailedEmailForm) => void;
};

export const CRMCaseEmailSingleViewUnresolved = (props: React.PropsWithChildren<TCaseEmailSingleViewUnresolvedProps>): JSX.Element => {

    const [viewStatus, setViewStatus] = useState<TEmailViewStatus>("email-body");
    const {
        resolutionStatus,
        resolutionData,
        setResolution,
        resetResolution,
    } = useResolutionStatus<TResolutionStatus, TResolutionData>({
        defaultStatus: "none",
        onSet: () => setViewStatus("email-body")
    });

    const hasDetailedEmail = () => !!props.email.children.detailed_email_form.children.email.id;

    const isResolved = () => props.email.original.resolved_at !== null;

    const canFileAttachments = () =>
        hasFilterableAttachments(props.email.children.detailed_email_form.children.email.attachments);

    const onDisassociate = () => {
        props.onDisassociate(props.email.children.detailed_email_form.children.disassociate_from_case_form);
        resetResolution();
    }

    const onChangeChaseModeForm = (form: TCaseUpdateChaseModeForm) => props.onChangeChaseModeForm({
        ...props.email.children.detailed_email_form.children.mark_as_resolved_form,
        edited: {
            ...props.email.children.detailed_email_form.children.mark_as_resolved_form.edited,
            case_chase_mode_form: {
                ...form
            },
        }
    })

    const onResolve = () => {
        props.onResolve({
            ...props.email.children.detailed_email_form.children.mark_as_resolved_form,
        });
        resetResolution();
    }

    return (
        <div className={`crm-email-single-view-resolved`}>
            
            {/* POPUP - UPDATE CHASE MODE */}
            {viewStatus === "update-chase-mode"
                && props.email.children.detailed_email_form.children.mark_as_resolved_form.edited.case_chase_mode_form
                && <CRMUpdateChaseModeSectionPopout
                    email={props.email}
                    chaseForm={props.email.children.detailed_email_form.children.mark_as_resolved_form.edited.case_chase_mode_form}
                    onChangeChaseModeForm={onChangeChaseModeForm}
                    onSubmit={setResolution("resolve")}
                    onClose={() => setViewStatus("email-body")}
                />
            }

            {/* BODY */}
            {viewStatus === "email-body" &&
                <CRMEmailSingleViewBody
                    {...props}
                />
            }
            
            {/* CONTROLS & COUNTDOWN NOTIFICATIONS */}
            {viewStatus === "email-body" &&
                <CRMBottomControlBar>
                    {resolutionStatus === "none" &&
                        <CRMEmailSingleViewControls
                            hideAllActions={! hasDetailedEmail()}
                            isShowingPrevious={props.isShowingPrevious}
                            isShowingNext={props.isShowingNext}

                            onDisassociate={setResolution("disassociate")}
                            onResolve={isResolved()
                                ? undefined
                                : props.email.children.detailed_email_form.children.mark_as_resolved_form.edited.case_chase_mode_form
                                    ? () => setViewStatus("update-chase-mode")
                                    : setResolution("resolve")
                            }
                            onReply={() => props.onReply(props.email.children.detailed_email_form)}
                            onForward={() => props.onForward(props.email.children.detailed_email_form)}
                            onFileAttachments={
                                canFileAttachments()
                                    ? () => props.onFileAttachments(props.email.children.detailed_email_form)
                                    : undefined
                            }

                            onPrevious={props.onPrevious}
                            onNext={props.onNext}
                        />
                    }

                    {resolutionStatus === "resolve" &&
                        <CRMConfirmationCountdown
                            title="Resolving!"
                            iconName="done-all"
                            primaryButtonMode="next"
                            duration={IRREVERSIBLE_CONFIRMATION_DURATION_MS}
                            onUndo={resetResolution}
                            onComplete={onResolve}
                        />
                    }

                    {resolutionStatus === "disassociate" &&
                        <CRMConfirmationCountdown
                            title="Disassociating!"
                            iconName="unfile"
                            primaryButtonMode="next"
                            onUndo={resetResolution}
                            onComplete={onDisassociate}
                        />
                    }
                </CRMBottomControlBar>
            }
        </div>
    );
};
